<template>
  <div>
    <v-dialog
      v-model="ConsentDialog"
      persistent
      max-width="780"
      :fullscreen="$vuetify.breakpoint.smAndDown"
    >
      <v-card class="dialog-card" v-if="!ApproveStatus">
        <div class="dialog-header">
          <span class="dialog-title">Visitor Verification</span>
        </div>
        <v-stepper v-model="step" flat class="stepper-modern">
          <v-stepper-header>
            <v-stepper-step step="1" :complete="step > 1">
              PDPA
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="2" :complete="step > 2">
              Face Verification
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step step="3" :complete="step > 3"
              >Confirm</v-stepper-step
            >
            <v-divider></v-divider>
            <v-stepper-step step="4">Complete</v-stepper-step>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content step="1">
              <div class="text-center mb-4">
                <v-icon size="60" color="indigo">mdi-shield-check</v-icon>
                <h2 class="mt-2 font-weight-bold">Privacy Consent</h2>
              </div>
              <div class="d-flex justify-center mb-3">
                <v-btn
                  width="110"
                  outlined
                  @click="changeLang('th')"
                  :color="pdpaLang === 'th' ? 'indigo' : ''"
                >
                  ไทย
                </v-btn>

                <v-btn
                  width="110"
                  outlined
                  class="ml-2"
                  @click="changeLang('en')"
                  :color="pdpaLang === 'en' ? 'indigo' : ''"
                >
                  English
                </v-btn>
              </div>
              <div class="pdpa-box" v-if="loadingPolicy">Loading...</div>
              <div class="pdpa-box" v-else v-html="pdpaContent"></div>
              <v-checkbox
                v-model="pdpaConsent"
                label="I agree to PDPA policy"
                class="mt-4 ml-3"
              />
              <div class="text-center mt-6">
                <v-btn
                  color="indigo"
                  large
                  elevation="3"
                  class="white--text"
                  :disabled="!pdpaConsent"
                  @click="step = 2"
                >
                  Continue
                </v-btn>
              </div>
            </v-stepper-content>
            <v-stepper-content step="2">
              <div class="text-center mb-4">
                <v-icon size="60" color="indigo"> mdi-face-recognition </v-icon>
                <h2 class="mt-2 font-weight-bold">Face Verification</h2>
              </div>
              <div class="camera-frame">
                <video ref="video" autoplay></video>
                <div class="face-overlay"></div>
              </div>
              <div class="d-flex justify-space-between mt-6">
                <v-btn outlined large @click="BackToStepOne()">
                  <v-icon left>mdi-arrow-left</v-icon>
                  Back
                </v-btn>
                <v-btn
                  color="indigo"
                  class="white--text"
                  large
                  elevation="3"
                  @click="capturePhoto"
                >
                  <v-icon left>mdi-camera</v-icon>
                  Take Photo
                </v-btn>
              </div>
            </v-stepper-content>
            <v-stepper-content step="3">
              <div class="confirm-wrapper">
                <h2 class="confirm-title">Confirm Your Information</h2>
                <v-row>
                  <v-col cols="12" md="5" class="text-center">
                    <div class="preview-frame-elevated">
                      <img :src="faceImage" />
                    </div>
                  </v-col>
                  <v-col cols="12" md="7">
                    <v-card class="info-card" elevation="2">
                      <v-card-text>
                        <div class="info-row">
                          <span class="label">Visitor Name</span>
                          <span class="value">{{ VisitorName }}</span>
                        </div>

                        <div class="info-row">
                          <span class="label">Invitation By</span>
                          <span class="value">{{ InvitationName }}</span>
                        </div>

                        <div class="info-row">
                          <span class="label">Tenant</span>
                          <span class="value">{{ TenantName }}</span>
                        </div>

                        <div class="info-row">
                          <span class="label">Time</span>
                          <span class="value highlight">
                            {{ StartDate }} - {{ ExpireDate }}
                          </span>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
                <div class="action-area">
                  <v-btn outlined large class="mr-4" @click="retakePhoto">
                    <v-icon left>mdi-camera-retake</v-icon>
                    Retake
                  </v-btn>
                  <v-btn
                    color="green"
                    class="white--text"
                    large
                    elevation="3"
                    @click="updateInvitePreRegister()"
                  >
                    <v-icon left>mdi-check</v-icon>
                    Confirm
                  </v-btn>
                </div>
              </div>
            </v-stepper-content>
            <v-stepper-content step="4">
              <div class="text-center success-area">
                <v-icon size="100" color="green"> mdi-check-circle </v-icon>
                <h2 class="mt-4 font-weight-bold">Registration Complete</h2>
                <p class="grey--text mt-2">Thank you</p>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card>
      <v-card class="dialog-card text-center pa-10" v-else>
        <v-icon size="100" color="grey">mdi-check-circle-outline</v-icon>
        <h2 class="mt-4 font-weight-bold" style="color: #444">
          {{ $t('message.AlreadyProcessed') }}
        </h2>
      </v-card>
    </v-dialog>

    <v-dialog v-model="LoadingDialog" persistent width="auto">
      <v-card
        class="pa-6 d-flex flex-column align-center justify-center"
        elevation="8"
        rounded="xl"
      >
        <v-progress-circular
          indeterminate
          color="indigo"
          size="70"
          width="6"
        ></v-progress-circular>
        <span class="mt-4 text-subtitle-1 font-weight-medium"
          >Loading, please wait...</span
        >
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";
import mammoth from "mammoth";
export default {
  name: "AcceptPreregister",
  components: {},
  data() {
    return {
      url: enurl.apiUrl,
      LoadingDialog: false,
      QRvalue: "",
      ConsentDialog: true,
      step: 1,
      pdpaConsent: false,
      faceImage: null,

      pdpaLang: "th",
      pdpaContent: "",
      loadingPolicy: false,
      requestId: 0,

      InviteID: null,
      InvitationName: "",
      VisitorName: "",
      TenantName: "",
      StartDate: "",
      ExpireDate: "",
      ApproveStatus: false,
      Active: false,
    };
  },

  watch: {
    step(val) {
      if (val === 2) {
        this.$nextTick(() => {
          this.startCamera();
        });
      } else {
        this.stopCamera();
      }
    },

    ConsentDialog(val) {
      if (!val) {
        this.stopCamera();
      }
    },

    pdpaLang: {
      handler() {
        this.loadPDPA();
      },
    },

    retakePhoto() {
      this.step = 2;
    },
  },

  beforeDestroy() {
    this.stopCamera();
  },

  async mounted() {
    let self = this;
    self.ConsentDialog = true;
    this.loadPDPA();
    let url = window.location.href;
    if (url != "") {
      let url_text = url.split("/");
      await self.getDataPreRegisterDetailByUid(url_text[url_text.length - 1]);
    }
  },

  methods: {
    async loadPDPA() {
      const currentRequest = ++this.requestId;
      try {
        const filename =
          this.pdpaLang === "th" ? "/PDPA_TH.docx" : "/PDPA_EN.docx";
        const response = await fetch(filename);
        const arrayBuffer = await response.arrayBuffer();
        const result = await mammoth.convertToHtml({
          arrayBuffer,
        });
        if (currentRequest !== this.requestId) return;
        this.pdpaContent = result.value;
      } catch (error) {
        console.error(error);
      }
    },

    changeLang(lang) {
      this.pdpaLang = lang;
    },

    startCamera() {
      this.stopCamera();
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          if (this.$refs.video) {
            this.$refs.video.srcObject = stream;
          }
        })
        .catch((err) => {
          console.error(err);
        });
    },

    stopCamera() {
      if (this.$refs.video && this.$refs.video.srcObject) {
        const tracks = this.$refs.video.srcObject.getTracks();
        tracks.forEach((track) => track.stop());
        this.$refs.video.srcObject = null;
      }
    },

    retakePhoto() {
      let self = this;
      self.step = 2;
      self.$nextTick(() => {
        self.startCamera();
      });
    },

    BackToStepOne() {
      let self = this;
      self.step = 1;
      self.stopCamera();
    },

    capturePhoto() {
      const video = this.$refs.video;
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0);
      this.faceImage = canvas.toDataURL("image/png");
      this.checkFaceImage();
    },

    async getDataPreRegisterDetailByUid(uid) {
      let self = this;
      self.LoadingDialog = true;
      let temp = {
        invite_uid: uid,
      };
      await axios
        .post(`${self.url}PreRegister/getDataPreRegisterDetailByUid`, temp)
        .then((response) => {
          if (response.data.status !== 0) return;
          self.BindingData(response.data.data);
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "เกิดข้อผิดพลาดในการเชื่อมต่อระบบ";

          Swal.fire({
            icon: "warning",
            width: 900,
            text: message,
          });
        })
        .finally(() => {
          self.LoadingDialog = false;
        });
    },

    BindingData(data) {
      let self = this;
      self.InviteID = data.inviteID;
      self.InvitationName = data.invitationName;
      self.VisitorName = data.guestInviteName;
      self.TenantName = data.inviteTenantName;
      self.StartDate = self.ChangeFormatDate(data.startDateTime);
      self.ExpireDate = self.ChangeFormatDate(data.endDateTime);
      self.ApproveStatus = data.approveStatus;
      self.Active = data.active;
    },

    checkFaceImage() {
      let self = this;
      self.LoadingDialog = true;
      let temp = {
        faceImage: self.faceImage,
      };
      axios
        .post(`${self.url}PreRegister/checkFaceImage`, temp)
        .then((response) => {
          if (response.data.status == 0) {
            self.stopCamera();
            self.step = 3;
          } else if (response.data.status == 1) {
            Swal.fire({
              icon: "warning",
              width: 900,
              text: response.data.message,
            });
          }
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "เกิดข้อผิดพลาดในการเชื่อมต่อระบบ";

          Swal.fire({
            icon: "warning",
            width: 900,
            text: message,
          });
        })
        .finally(() => {
          self.LoadingDialog = false;
        });
    },

    updateInvitePreRegister() {
      let self = this;
      self.stopCamera();
      let temp = {
        inviteId: self.InviteID,
        faceImage: self.faceImage,
      };
      self.LoadingDialog = true;
      axios
        .post(`${self.url}PreRegister/updateInvitePreRegister`, temp)
        .then((response) => {
          if (response.data.status == 0) {
            self.stopCamera();
            self.step = 4;
          } else if (response.data.status == 1) {
            Swal.fire({
              icon: "warning",
              width: 900,
              text: response.data.message,
            });
          }
        })
        .catch((error) => {
          const message =
            error?.response?.data?.message ||
            error?.message ||
            "เกิดข้อผิดพลาดในการเชื่อมต่อระบบ";

          Swal.fire({
            icon: "warning",
            width: 900,
            text: message,
          });
        })
        .finally(() => {
          self.LoadingDialog = false;
        });
    },

    ChangeFormatDate(date) {
      if (!date) return null;
      var DateData = new Date(date);
      var hours = DateData.getHours();
      var minutes = DateData.getMinutes();
      minutes = minutes < 10 ? "0" + minutes : minutes;
      var strTime = hours + ":" + minutes;
      return (
        DateData.getDate() +
        "/" +
        (DateData.getMonth() + 1) +
        "/" +
        DateData.getFullYear() +
        "  " +
        strTime
      );
    },
  },
};
</script>
<style scoped>
.dialog-card {
  border-radius: 16px;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #3f51b5, #5c6bc0);
  color: white;
  font-size: 20px;
  font-weight: 600;
  padding: 18px 24px;
  display: flex;
  align-items: center;
}

.dialog-title {
  font-size: 20px;
}

.stepper-modern {
  padding: 20px;
}

.camera-frame {
  position: relative;
  height: 340px;
  border-radius: 14px;
  overflow: hidden;
  border: 2px solid #e0e0e0;
  background: #000;
}

.camera-frame video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.face-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 220px;
  height: 280px;
  border: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 140px;
}

.success-area {
  padding: 40px 0;
}

.preview-frame {
  width: 260px;
  height: 320px;
  margin: auto;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #ddd;
}

.preview-frame img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.pdpa-box {
  max-height: 280px;
  overflow-y: auto;
  padding: 16px;
  border: 1px solid #ddd;
  border-radius: 12px;
  background: #fafafa;
  text-align: left;
}

.confirm-wrapper {
  padding: 10px 10px 20px;
}

.confirm-title {
  text-align: center;
  font-weight: 700;
  margin-bottom: 20px;
  color: #333;
}

/* 👤 รูป */
.preview-frame-elevated {
  width: 260px;
  height: 320px;
  margin: auto;
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid #eee;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
}

.preview-frame-elevated img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.info-card {
  border-radius: 16px;
  padding: 10px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px dashed #eee;
}

.info-row:last-child {
  border-bottom: none;
}

.label {
  color: #888;
  font-size: 16px;
}

.value {
  font-weight: 600;
  font-size: 16px;
  color: #222;
}

.highlight {
  color: #3f51b5;
}

.action-area {
  display: flex;
  justify-content: center;
  margin-top: 25px;
}
</style>
    